/**
 * Statistics tab for the Contacts (Marketing/Leads) page.
 * Shows: conversion-rate KPIs, by-channel breakdown, by-product-group
 * breakdown, and by-assignee table.
 * All data from verified live endpoints — no mocks.
 */
import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, DataTable, EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { BarChart2 } from 'lucide-react';
import {
  useConversionRateStats,
  useChannelStats,
  useProductGroupStats,
  useAssigneeStats,
} from '@/api/contacts';

/* ── Conversion rate KPIs ─────────────────────────────────────────────── */

function ConversionRateSection() {
  const { data, isLoading, isError } = useConversionRateStats();
  const stats = data?.result;

  if (isError) return <Alert tone="danger" title="Failed to load conversion rate stats" />;

  const kpis = [
    { label: 'Total Leads', value: stats?.total_leads ?? 0 },
    { label: 'Classified', value: stats?.total_classified ?? 0 },
    { label: 'Classification Rate', value: stats ? `${stats.classification_rate.toFixed(1)}%` : '0%' },
    { label: 'Warm Lead Rate', value: stats ? `${stats.warm_lead_rate.toFixed(1)}%` : '0%' },
    { label: 'SQL Rate', value: stats ? `${stats.sale_qualified_lead_rate.toFixed(1)}%` : '0%' },
    { label: 'Unqualified Rate', value: stats ? `${stats.un_qualified_rate.toFixed(1)}%` : '0%' },
  ] as const;

  return (
    <Card flat>
      <CardBody>
        {isLoading ? (
          <StatGroup columns={6}>
            {kpis.map((_, i) => <Skeleton key={i} style={{ height: 56 }} />)}
          </StatGroup>
        ) : (
          <StatGroup columns={6}>
            {kpis.map((k) => (
              <Stat
                key={k.label}
                label={k.label}
                value={typeof k.value === 'number' ? k.value.toLocaleString('en-US') : k.value}
              />
            ))}
          </StatGroup>
        )}
      </CardBody>
    </Card>
  );
}

/* ── By-channel table ─────────────────────────────────────────────────── */

function ByChannelSection() {
  const { data, isLoading, isError } = useChannelStats();

  if (isError) return <Alert tone="danger" title="Failed to load channel stats" />;
  if (isLoading) return <Card flat><CardBody><Skeleton style={{ height: 120 }} /></CardBody></Card>;

  return (
    <Card flat>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={[
            { id: 'channel', header: 'Channel', cell: ({ row }) => row.original.channel || '—' },
            { id: 'total', header: 'Total', cell: ({ row }) => row.original.total.toLocaleString('en-US') },
            { id: 'unclassified', header: 'Unclassified', cell: ({ row }) => row.original.unclassified.toLocaleString('en-US') },
            { id: 'warm_lead', header: 'Warm Lead', cell: ({ row }) => row.original.warm_lead.toLocaleString('en-US') },
            { id: 'sale_qualified_lead', header: 'SQL', cell: ({ row }) => row.original.sale_qualified_lead.toLocaleString('en-US') },
            { id: 'cool_lead', header: 'Cool Lead', cell: ({ row }) => row.original.cool_lead.toLocaleString('en-US') },
            { id: 'un_qualified_lead', header: 'Unqualified', cell: ({ row }) => row.original.un_qualified_lead.toLocaleString('en-US') },
          ]}
          data={data?.results ?? []}
          getRowId={(r) => r.channel}
          emptyState={<EmptyState icon={<BarChart2 size={24} />} title="No channel data" />}
        />
      </CardBody>
    </Card>
  );
}

/* ── By-product-group table ───────────────────────────────────────────── */

function ByProductGroupSection() {
  const { data, isLoading, isError } = useProductGroupStats();

  if (isError) return <Alert tone="danger" title="Failed to load product group stats" />;
  if (isLoading) return <Card flat><CardBody><Skeleton style={{ height: 120 }} /></CardBody></Card>;

  return (
    <Card flat>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={[
            { id: 'product_group', header: 'Product Group', cell: ({ row }) => row.original.product_group || '—' },
            { id: 'total', header: 'Total', cell: ({ row }) => row.original.total.toLocaleString('en-US') },
            { id: 'unclassified', header: 'Unclassified', cell: ({ row }) => row.original.unclassified.toLocaleString('en-US') },
            { id: 'warm_lead', header: 'Warm Lead', cell: ({ row }) => row.original.warm_lead.toLocaleString('en-US') },
            { id: 'sale_qualified_lead', header: 'SQL', cell: ({ row }) => row.original.sale_qualified_lead.toLocaleString('en-US') },
            { id: 'cool_lead', header: 'Cool Lead', cell: ({ row }) => row.original.cool_lead.toLocaleString('en-US') },
            { id: 'un_qualified_lead', header: 'Unqualified', cell: ({ row }) => row.original.un_qualified_lead.toLocaleString('en-US') },
          ]}
          data={data?.results ?? []}
          getRowId={(r) => r.product_group}
          emptyState={<EmptyState icon={<BarChart2 size={24} />} title="No product group data" />}
        />
      </CardBody>
    </Card>
  );
}

/* ── By-assignee table ────────────────────────────────────────────────── */

function ByAssigneeSection() {
  const { data, isLoading, isError } = useAssigneeStats();

  if (isError) return <Alert tone="danger" title="Failed to load assignee stats" />;
  if (isLoading) return <Card flat><CardBody><Skeleton style={{ height: 120 }} /></CardBody></Card>;

  return (
    <Card flat>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={[
            { id: 'employee_name', header: 'Assignee', cell: ({ row }) => row.original.employee_name || '—' },
            { id: 'total', header: 'Total', cell: ({ row }) => row.original.total.toLocaleString('en-US') },
            { id: 'unclassified', header: 'Unclassified', cell: ({ row }) => row.original.unclassified.toLocaleString('en-US') },
            { id: 'warm_lead', header: 'Warm Lead', cell: ({ row }) => row.original.warm_lead.toLocaleString('en-US') },
            { id: 'sale_qualified_lead', header: 'SQL', cell: ({ row }) => row.original.sale_qualified_lead.toLocaleString('en-US') },
            { id: 'cool_lead', header: 'Cool Lead', cell: ({ row }) => row.original.cool_lead.toLocaleString('en-US') },
            { id: 'un_qualified_lead', header: 'Unqualified', cell: ({ row }) => row.original.un_qualified_lead.toLocaleString('en-US') },
          ]}
          data={data?.results ?? []}
          getRowId={(r) => String(r.employee_id)}
          emptyState={<EmptyState icon={<BarChart2 size={24} />} title="No assignee data" />}
        />
      </CardBody>
    </Card>
  );
}

/* ── Tab root ─────────────────────────────────────────────────────────── */

export function ContactStatisticsTab() {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <ConversionRateSection />
      <ByChannelSection />
      <ByProductGroupSection />
      <ByAssigneeSection />
    </div>
  );
}
